import React, { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';

interface PrConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'primary';
  isSubmitting?: boolean;
}

export const PrConfirmationModal: React.FC<PrConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'primary',
  isSubmitting = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Body scroll lock & ESC key close listener
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isSubmitting) onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Visual De-Emphasis Backdrop */}
      <div
        className="fixed inset-0 bg-slate-950/80 backdrop-blur-md transition-opacity animate-in fade-in"
        onClick={() => !isSubmitting && onClose()}
      />

      {/* Elevated Sharp Modal Container */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        className="relative z-50 w-full max-w-md rounded-2xl border border-border bg-surface dark:bg-[#0e101f] text-foreground p-6 shadow-2xl space-y-4 font-heading animate-in zoom-in-95 duration-150"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`p-3 rounded-2xl border ${
                variant === 'danger'
                  ? 'bg-rose-500/10 text-rose-600 border-rose-500/20'
                  : variant === 'warning'
                  ? 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                  : 'bg-primary/10 text-primary border-primary/20'
              }`}
            >
              {variant === 'danger' || variant === 'warning' ? (
                <AlertTriangle className="h-5 w-5" />
              ) : (
                <CheckCircle2 className="h-5 w-5" />
              )}
            </div>

            <div>
              <h3 className="font-heading text-base font-extrabold text-foreground">{title}</h3>
              <p className="text-xs text-foreground-secondary font-medium mt-0.5">{description}</p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            disabled={isSubmitting}
            onClick={onClose}
            className="h-8 w-8 p-0 rounded-full text-foreground-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" disabled={isSubmitting} onClick={onClose} className="rounded-xl font-bold">
            {cancelText}
          </Button>

          <Button
            disabled={isSubmitting}
            onClick={onConfirm}
            className={`rounded-xl font-bold text-white ${
              variant === 'danger'
                ? 'bg-rose-600 hover:bg-rose-700'
                : variant === 'warning'
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {isSubmitting ? 'Processing...' : confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};
